import Phaser from 'phaser';
import type { VFXSystem, DamageNumberType } from '../systems/VFXSystem';

export interface EnemyParams {
  hp: number;
  speed: number;
  damage: number;
  size: number;
  color: number;
}

/** @deprecated Use EnemyParams instead */
export type EnemyConfig = EnemyParams;

/** Base enemy: coloured circle that chases the player. */
export class Enemy extends Phaser.GameObjects.Container {
  readonly maxHp: number;
  hp: number;
  readonly speed: number;
  readonly damage: number;

  protected circle: Phaser.GameObjects.Arc;

  private hpBarBg: Phaser.GameObjects.Rectangle;
  private hpBar: Phaser.GameObjects.Rectangle;

  protected readonly barWidth: number;
  private readonly barHeight = 4;
  protected readonly barOffsetY: number;
  private readonly bodyColor: number;

  constructor(scene: Phaser.Scene, x: number, y: number, params: EnemyParams) {
    super(scene, x, y);

    this.maxHp = params.hp;
    this.hp = params.hp;
    this.speed = params.speed;
    this.damage = params.damage;

    this.barOffsetY = -(params.size + 8);
    this.bodyColor = params.color;

    // Body
    this.circle = scene.add.arc(0, 0, params.size, 0, 360, false, params.color);
    this.add(this.circle);

    // HP bar background (dark)
    this.hpBarBg = scene.add.rectangle(0, this.barOffsetY, this.barWidth, this.barHeight, 0x333333);
    this.hpBarBg.setOrigin(0.5, 0.5);
    this.add(this.hpBarBg);

    // HP bar fill (green → red)
    this.hpBar = scene.add.rectangle(
      -this.barWidth / 2,
      this.barOffsetY,
      this.barWidth,
      this.barHeight,
      0x44ff44,
    );
    this.hpBar.setOrigin(0, 0.5);
    this.add(this.hpBar);

    scene.add.existing(this);

    this.setScale(0.6);
    this.setAlpha(0);
    scene.tweens.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 220,
      ease: 'Back.Out',
    });
  }

  /** Apply damage to this enemy. Returns true when the enemy dies. */
  takeDamage(amount: number, isCrit = false): boolean {
    this.hp = Math.max(0, this.hp - amount);
    this.updateHpBar();
    this.flashWhite();

    // Floating damage number
    const vfx = this.scene.registry.get('vfx') as VFXSystem | undefined;
    const numType: DamageNumberType = isCrit ? 'crit' : 'normal';
    vfx?.showDamageNumber(this.x, this.y + this.barOffsetY - 8, amount, numType);

    return this.hp <= 0;
  }

  /** Move toward player position. Call from WaveManager.update(). */
  update(targetX: number, targetY: number, delta: number): void {
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return;
    const step = this.speed * (delta / 1000);
    this.x += (dx / dist) * step;
    this.y += (dy / dist) * step;
  }

  private updateHpBar(): void {
    const ratio = this.hp / this.maxHp;
    this.hpBar.width = this.barWidth * ratio;
    const r = Math.floor(255 * (1 - ratio));
    const g = Math.floor(255 * ratio);
    this.hpBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 0));
  }

  /** Brief white flash to indicate a hit. */
  private flashWhite(): void {
    this.circle.setFillStyle(0xffffff);
    this.scene.time.delayedCall(80, () => {
      if (this.active) {
        this.circle.setFillStyle(this.bodyColor);
      }
    });
  }

  /** Spawn a colored particle burst then destroy. */
  die(): void {
    const vfx = this.scene.registry.get('vfx') as VFXSystem | undefined;
    if (vfx) {
      vfx.enemyDeathParticles(this.x, this.y, this.bodyColor);
    } else {
      // Fallback: plain particle emitter
      const particles = this.scene.add.particles(this.x, this.y, undefined, {
        speed: { min: 40, max: 120 },
        scale: { start: 0.4, end: 0 },
        lifespan: 400,
        quantity: 12,
        tint: 0xffd700,
        gravityY: 60,
      });
      this.scene.time.delayedCall(500, () => particles.destroy());
    }
    this.destroy();
  }

  destroy(fromScene?: boolean): void {
    super.destroy(fromScene);
  }
}
